import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  MapPin, 
  Search, 
  Plus, 
  Trash2, 
  Upload, 
  Download,
  CheckCircle,
  XCircle,
  Loader2,
  Settings,
  Truck,
  FileText,
  Zap,
  AlertCircle,
  Edit2,
  Save,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ====== Serviceability Check Component ======
const ServiceabilityChecker = () => {
  const [pincode, setPincode] = useState('');
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);

  const checkServiceability = async () => {
    if (!pincode || pincode.length < 5) {
      toast.error('Please enter a valid pincode');
      return;
    }

    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/serviceability/check`,
        { pincode, is_cod: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check serviceability');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <div className="flex-1 max-w-xs">
          <Label className="text-wt-text-primary">Enter Pincode</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="e.g., 110001"
              className="bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="pincode-input"
              onKeyDown={(e) => e.key === 'Enter' && checkServiceability()}
            />
            <Button
              onClick={checkServiceability}
              disabled={checking}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              data-testid="check-serviceability-btn"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {results && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-wt-text-primary mb-4">
            Results for Pincode: {results.pincode}
          </h3>
          
          {results.serviceable_couriers?.length > 0 ? (
            <div className="mb-4">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle className="h-3 w-3 mr-1" />
                Serviceable by {results.serviceable_couriers.length} courier(s)
              </Badge>
            </div>
          ) : (
            <div className="mb-4">
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <XCircle className="h-3 w-3 mr-1" />
                Not serviceable
              </Badge>
            </div>
          )}

          <div className="space-y-3">
            {results.results?.map((result, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border ${
                  result.serviceable 
                    ? 'bg-green-500/5 border-green-500/20' 
                    : 'bg-red-500/5 border-red-500/20'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Truck className={`h-5 w-5 ${result.serviceable ? 'text-green-400' : 'text-red-400'}`} />
                    <span className="font-medium text-wt-text-primary capitalize">
                      {result.courier_type}
                    </span>
                  </div>
                  {result.serviceable ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400" />
                  )}
                </div>
                
                {result.serviceable && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                    <div>
                      <span className="text-wt-text-muted">City</span>
                      <p className="text-wt-text-primary">{result.city || '-'}</p>
                    </div>
                    <div>
                      <span className="text-wt-text-muted">State</span>
                      <p className="text-wt-text-primary">{result.state || '-'}</p>
                    </div>
                    <div>
                      <span className="text-wt-text-muted">COD Available</span>
                      <p className={result.cod ? 'text-green-400' : 'text-red-400'}>
                        {result.cod ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <span className="text-wt-text-muted">Prepaid</span>
                      <p className={result.prepaid ? 'text-green-400' : 'text-red-400'}>
                        {result.prepaid ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                )}
                
                {result.error && (
                  <p className="text-sm text-red-400 mt-2">{result.error}</p>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ====== Manual Pincode Management ======
const ManualPincodeManager = () => {
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPincodes, setNewPincodes] = useState('');
  const [selectedCourier, setSelectedCourier] = useState('delhivery');
  const [addingPincodes, setAddingPincodes] = useState(false);

  useEffect(() => {
    fetchPincodes();
  }, []);

  const fetchPincodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/serviceability/pincodes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPincodes(response.data.pincodes || []);
    } catch (error) {
      console.error('Error fetching pincodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPincodes = async () => {
    if (!newPincodes.trim()) {
      toast.error('Please enter pincodes');
      return;
    }

    const pincodeList = newPincodes
      .split(/[\n,\s]+/)
      .map(p => p.trim())
      .filter(p => /^\d{5,6}$/.test(p));

    if (pincodeList.length === 0) {
      toast.error('No valid pincodes found');
      return;
    }

    setAddingPincodes(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/couriers/serviceability/pincodes`,
        { 
          pincodes: pincodeList, 
          courier_type: selectedCourier,
          cod_enabled: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Added ${pincodeList.length} pincodes`);
      setNewPincodes('');
      fetchPincodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add pincodes');
    } finally {
      setAddingPincodes(false);
    }
  };

  const groupedPincodes = pincodes.reduce((acc, p) => {
    const courier = p.courier_type || 'unknown';
    if (!acc[courier]) acc[courier] = [];
    acc[courier].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Pincodes */}
      <div className="bg-wt-surface border border-wt-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-wt-gold" />
          Add Serviceable Pincodes
        </h3>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-wt-text-primary">Courier</Label>
            <Select value={selectedCourier} onValueChange={setSelectedCourier}>
              <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-surface border-wt-border">
                <SelectItem value="delhivery">Delhivery</SelectItem>
                <SelectItem value="bluedart">Bluedart</SelectItem>
                <SelectItem value="dtdc">DTDC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-wt-text-primary">Pincodes (one per line or comma-separated)</Label>
            <textarea
              value={newPincodes}
              onChange={(e) => setNewPincodes(e.target.value)}
              placeholder="110001&#10;110002&#10;110003"
              className="mt-1 w-full h-24 px-3 py-2 bg-wt-bg border border-wt-border rounded-md text-wt-text-primary resize-none"
            />
          </div>
        </div>
        
        <Button
          onClick={handleAddPincodes}
          disabled={addingPincodes}
          className="mt-4 bg-wt-gold hover:bg-wt-gold-dark text-black"
        >
          {addingPincodes ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Add Pincodes
        </Button>
      </div>

      {/* Pincode List */}
      <div className="bg-wt-surface border border-wt-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
            <MapPin className="h-5 w-5 text-wt-gold" />
            Manual Pincode List
          </h3>
          <Badge className="bg-wt-subtle text-wt-text-secondary">
            {pincodes.length} pincodes
          </Badge>
        </div>

        {Object.keys(groupedPincodes).length === 0 ? (
          <p className="text-wt-text-muted text-center py-8">
            No manual pincodes configured. Add pincodes above or use API-based serviceability checks.
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedPincodes).map(([courier, courierPincodes]) => (
              <div key={courier} className="border border-wt-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="h-4 w-4 text-wt-gold" />
                  <span className="font-medium text-wt-text-primary capitalize">{courier}</span>
                  <Badge className="bg-wt-subtle text-wt-text-muted text-xs">
                    {courierPincodes.length}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {courierPincodes.slice(0, 50).map((p, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 bg-wt-bg border border-wt-border rounded text-sm text-wt-text-secondary"
                    >
                      {p.pincode}
                    </span>
                  ))}
                  {courierPincodes.length > 50 && (
                    <span className="px-2 py-1 text-sm text-wt-text-muted">
                      +{courierPincodes.length - 50} more
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ====== Auto-Assign Rules ======
const AutoAssignRules = () => {
  const [rules, setRules] = useState([]);
  const [courierIntegrations, setCourierIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    courier_integration_id: '',
    priority: 0,
    conditions: [{ field: 'pincode', operator: 'starts_with', value: '' }]
  });

  useEffect(() => {
    fetchRules();
    fetchCourierIntegrations();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/rules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourierIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/integrations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCourierIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error fetching courier integrations:', error);
    }
  };

  const handleSaveRule = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a rule name');
      return;
    }

    if (!formData.courier_integration_id) {
      toast.error('Please select a courier');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingRule) {
        await axios.put(
          `${API_BASE_URL}/couriers/rules/${editingRule.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rule updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/couriers/rules`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rule created');
      }
      
      setShowAddDialog(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/rules/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      courier_integration_id: '',
      priority: 0,
      conditions: [{ field: 'pincode', operator: 'starts_with', value: '' }]
    });
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'pincode', operator: 'starts_with', value: '' }]
    }));
  };

  // Get the selected integration name for display
  const getIntegrationName = (integrationId) => {
    const integration = courierIntegrations.find(i => i.id === integrationId);
    return integration ? integration.name : integrationId;
  };

  // Get operator options based on field type
  const getOperatorOptions = (field) => {
    if (field === 'total_price' || field === 'weight') {
      return [
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'equals', label: 'Equals' },
        { value: 'between', label: 'Between' }
      ];
    }
    return [
      { value: 'starts_with', label: 'Starts With' },
      { value: 'equals', label: 'Equals' },
      { value: 'in_list', label: 'In List' }
    ];
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Auto-Assign Rules</h3>
          <p className="text-sm text-wt-text-muted">
            Automatically assign couriers based on pincode, payment mode, etc.
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingRule(null);
            setShowAddDialog(true);
          }}
          className="bg-wt-gold hover:bg-wt-gold-dark text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="bg-wt-surface border border-wt-border rounded-xl p-8 text-center">
          <Zap className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
          <h4 className="text-lg font-medium text-wt-text-primary mb-2">No Rules Configured</h4>
          <p className="text-wt-text-muted mb-4">
            Create rules to automatically assign couriers based on order criteria
          </p>
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="outline"
            className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
          >
            Create First Rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.sort((a, b) => a.priority - b.priority).map((rule, idx) => (
            <motion.div
              key={rule.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-wt-surface border border-wt-border rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className="bg-wt-subtle text-wt-text-muted">
                      Priority {rule.priority}
                    </Badge>
                    <span className="font-medium text-wt-text-primary">{rule.name}</span>
                    <Badge className={rule.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-wt-text-muted">Assign to:</span>
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 capitalize">
                      <Truck className="h-3 w-3 mr-1" />
                      {rule.courier_integration_name || getIntegrationName(rule.courier_integration_id) || rule.courier_type}
                    </Badge>
                  </div>
                  
                  {rule.conditions?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <span className="text-xs text-wt-text-muted">Conditions:</span>
                      {rule.conditions.map((cond, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-wt-text-secondary">
                          <span className="capitalize">{cond.field}</span>
                          <span className="text-wt-text-muted">{cond.operator.replace('_', ' ')}</span>
                          <span className="text-wt-gold">{cond.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingRule(rule);
                      setFormData({
                        name: rule.name,
                        courier_integration_id: rule.courier_integration_id || '',
                        priority: rule.priority,
                        conditions: rule.conditions || []
                      });
                      setShowAddDialog(true);
                    }}
                    className="text-wt-text-muted hover:text-wt-text-primary"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Rule Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Zap className="h-5 w-5 text-wt-gold" />
              {editingRule ? 'Edit Rule' : 'Create Auto-Assign Rule'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Rule Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Delhi Zone Delhivery"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              
              <div>
                <Label className="text-wt-text-primary">Priority</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted mt-1">Lower = higher priority</p>
              </div>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Assign to Courier</Label>
              <Select 
                value={formData.courier_integration_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, courier_integration_id: v }))}
              >
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue placeholder="Select courier account" />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  {courierIntegrations.length === 0 ? (
                    <SelectItem value="none" disabled>No couriers configured</SelectItem>
                  ) : (
                    courierIntegrations.map((integration) => (
                      <SelectItem key={integration.id} value={integration.id}>
                        <div className="flex items-center gap-2">
                          <span className="capitalize">{integration.courier_type}</span>
                          <span className="text-wt-text-muted">-</span>
                          <span>{integration.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {courierIntegrations.length === 0 && (
                <p className="text-xs text-amber-400 mt-1">
                  No couriers configured. Add courier integrations first.
                </p>
              )}
            </div>
            
            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-wt-text-primary">Conditions</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addCondition}
                  className="text-wt-gold hover:bg-wt-gold/10"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.conditions.map((cond, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-wt-bg rounded-lg border border-wt-border">
                    <Select 
                      value={cond.field} 
                      onValueChange={(v) => {
                        // Reset operator when field changes
                        const defaultOp = (v === 'total_price' || v === 'weight') ? 'greater_than' : 'starts_with';
                        updateCondition(idx, 'field', v);
                        updateCondition(idx, 'operator', defaultOp);
                      }}
                    >
                      <SelectTrigger className="w-40 bg-wt-surface border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="pincode">Pincode</SelectItem>
                        <SelectItem value="payment_mode">Payment Mode</SelectItem>
                        <SelectItem value="state">State</SelectItem>
                        <SelectItem value="total_price">Total Price</SelectItem>
                        <SelectItem value="weight">Order Weight</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={cond.operator} 
                      onValueChange={(v) => updateCondition(idx, 'operator', v)}
                    >
                      <SelectTrigger className="w-36 bg-wt-surface border-wt-border text-wt-text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        {getOperatorOptions(cond.field).map(op => (
                          <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {cond.operator === 'between' ? (
                      <div className="flex items-center gap-1 flex-1">
                        <Input
                          type="number"
                          value={cond.value_min || ''}
                          onChange={(e) => updateCondition(idx, 'value_min', e.target.value)}
                          placeholder="Min"
                          className="w-24 bg-wt-surface border-wt-border text-wt-text-primary"
                        />
                        <span className="text-wt-text-muted">to</span>
                        <Input
                          type="number"
                          value={cond.value_max || ''}
                          onChange={(e) => updateCondition(idx, 'value_max', e.target.value)}
                          placeholder="Max"
                          className="w-24 bg-wt-surface border-wt-border text-wt-text-primary"
                        />
                      </div>
                    ) : (
                      <Input
                        type={cond.field === 'total_price' || cond.field === 'weight' ? 'number' : 'text'}
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                        placeholder={
                          cond.field === 'payment_mode' ? 'cod or prepaid' : 
                          cond.field === 'total_price' ? 'Amount' :
                          cond.field === 'weight' ? 'Weight (kg)' :
                          'Value'
                        }
                        className="flex-1 bg-wt-surface border-wt-border text-wt-text-primary"
                      />
                    )}
                    
                    {formData.conditions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCondition(idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowAddDialog(false);
                  setEditingRule(null);
                }}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveRule}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ====== Main Page Component ======
const CourierSettingsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/integrations')}
          className="border-wt-border"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-600">
            <Settings className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Courier Settings</h1>
            <p className="text-wt-text-muted">Serviceability & Auto-Assign Rules</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="ml-auto border-wt-border"
          onClick={() => navigate('/integrations/shipping-rate-cards')}
          data-testid="open-shipping-rate-cards-btn"
        >
          Shipping Rate Cards
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="serviceability" className="space-y-6">
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="serviceability" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Search className="h-4 w-4 mr-2" />
            Serviceability Check
          </TabsTrigger>
          <TabsTrigger value="pincodes" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <MapPin className="h-4 w-4 mr-2" />
            Manual Pincodes
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
            <Zap className="h-4 w-4 mr-2" />
            Auto-Assign Rules
          </TabsTrigger>
        </TabsList>

        <TabsContent value="serviceability">
          <ServiceabilityChecker />
        </TabsContent>

        <TabsContent value="pincodes">
          <ManualPincodeManager />
        </TabsContent>

        <TabsContent value="rules">
          <AutoAssignRules />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CourierSettingsPage;
